import { RankKit } from "@/components/site/RankKit";
import { HUD_DISPLAY_FY } from "@/data/manual-facts";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { SiteShell, PageHero } from "@/components/site/SiteShell";
import heroDscrCalc from "@/assets/hero/hero-dscr-calculator-couch.jpg";
import { Section, CTABand } from "@/components/site/Blocks";
import { LiveDscrRateSection } from "@/components/rates/LiveDscrRateSection";
import { getFmrByZip, getFmrByFips, listCounties, type FmrResult } from "@/lib/fmr.functions";
import { UniversalLeadForm as LeadForm } from "@/components/site/UniversalLeadForm";
import { submitLead, type LeadInput } from "@/lib/leads.functions";
import { captureUtmParams } from "@/lib/utm";
import { buildHead } from "@/lib/seo-head";
import { STATE_RATES, getStateRate } from "@/data/stateTaxRates";
import { PageEssentials, PageOutro } from "@/components/site/PageEssentials";

export const Route = createFileRoute("/dscr-calculator")({
  head: () =>
    buildHead({
      title: "DSCR Calculator | Rental Loans | Simply Approved",
      description:
        "Free DSCR loan calculator with live HUD Fair Market Rents. Model LTR, STR, and Section 8 rentals with IO toggle, cash-on-cash, and cap rate.",
      url: "/dscr-calculator",
      keywords:
        "DSCR calculator, DSCR loan calculator, debt service coverage ratio calculator, rental property DSCR calculator, Section 8 DSCR calculator, Airbnb DSCR calculator, cash on cash return calculator, cap rate calculator, DSCR ratio formula, rental income loan calculator, interest only DSCR calculator, HUD FMR calculator",
      breadcrumbs: [
        { name: "Home", url: "/" },
        { name: "DSCR Calculator", url: "/dscr-calculator" },
      ],
      service: { name: "DSCR Loan Calculator" },
      extraJsonLd: [
        {
          "@context": "https://schema.org",
          "@type": "WebApplication",
          name: "DSCR Calculator",
          applicationCategory: "FinanceApplication",
          operatingSystem: "Web",
          url: "/dscr-calculator",
          description:
            "Free DSCR loan calculator with live HUD Fair Market Rents for LTR, STR, and Section 8 rental scenarios.",
          offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
        },
      ],
    }),
  component: Calc,
});

type Mode = "ltr" | "str" | "section8";
type Bedrooms = "studio" | "oneBr" | "twoBr" | "threeBr" | "fourBr";
type LookupType = "zip" | "county";

const BR_LABEL: Record<Bedrooms, string> = {
  studio: "Studio",
  oneBr: "1 BR",
  twoBr: "2 BR",
  threeBr: "3 BR",
  fourBr: "4 BR",
};
const PREPAY_OPTIONS = [
  { value: "5-4-3-2-1", label: "5/4/3/2/1 (best rate)" },
  { value: "3-2-1", label: "3/2/1" },
  { value: "3-year", label: "3-yr step-down" },
  { value: "none", label: "None (+0.375%)" },
];

type Scenario = {
  mode: Mode;
  price: number;
  downPct: number;
  rate: number;
  termYears: number;
  ioYears: number;
  closingCostsPct: number;
  prepay: string;
  stateCode: string;
  autoTax: boolean;
  taxesAnnual: number;
  insAnnual: number;
  hoaMonthly: number;
  ltrRent: number;
  ltrMultiplier: number;
  vacancyPct: number;
  mgmtPct: number;
  adr: number;
  occupancyPct: number;
  strMgmtPct: number;
  strOpexMonthly: number;
  strReservesMonthly: number;
  lookupType: LookupType;
  zip: string;
  countyState: string;
  countyFips: string;
  bedrooms: Bedrooms;
  paymentStd: number;
  utilityAllowance: number;
  section8MgmtPct: number;
  section8ReservesMonthly: number;
};

const DEFAULT: Scenario = {
  mode: "ltr",
  price: 350000,
  downPct: 20,
  rate: 7.5,
  termYears: 30,
  ioYears: 0,
  closingCostsPct: 3,
  prepay: "5-4-3-2-1",
  stateCode: "",
  autoTax: false,
  taxesAnnual: 4200,
  insAnnual: 1400,
  hoaMonthly: 0,
  ltrRent: 2600,
  ltrMultiplier: 1.15,
  vacancyPct: 5,
  mgmtPct: 8,
  adr: 210,
  occupancyPct: 65,
  strMgmtPct: 20,
  strOpexMonthly: 350,
  strReservesMonthly: 150,
  lookupType: "zip",
  zip: "",
  countyState: "",
  countyFips: "",
  bedrooms: "threeBr",
  paymentStd: 100,
  utilityAllowance: 0,
  section8MgmtPct: 8,
  section8ReservesMonthly: 150,
};

const LEAD_UNLOCK_KEY = "dscr-calc:lead-unlocked";

function Calc() {
  const [s, setS] = useState<Scenario>(DEFAULT);
  const [fmr, setFmr] = useState<FmrResult | null>(null);
  const [shareCopied, setShareCopied] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [leadUnlocked, setLeadUnlocked] = useState(false);
  const [gate, setGate] = useState<null | "share" | "print" | "email">(null);

  // Hydrate unlock state from localStorage (per-browser, per-page).
  useEffect(() => {
    if (typeof window !== "undefined" && window.localStorage.getItem(LEAD_UNLOCK_KEY) === "1") {
      setLeadUnlocked(true);
    }
  }, []);

  const set = <K extends keyof Scenario>(k: K, v: Scenario[K]) => setS((prev) => ({ ...prev, [k]: v }));

  // ---- URL share: decode on mount ----
  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      const param = url.searchParams.get("s");
      if (!param) return;
      const decoded = JSON.parse(atob(param.replace(/-/g, "+").replace(/_/g, "/"))) as Partial<Scenario>;
      setS((prev) => ({ ...prev, ...decoded }));
    } catch {
      /* ignore malformed share links */
    }
  }, []);

  // ---- Auto tax/insurance from state ----
  useEffect(() => {
    if (!s.autoTax || !s.stateCode) return;
    const r = getStateRate(s.stateCode);
    if (!r) return;
    setS((prev) => ({
      ...prev,
      taxesAnnual: Math.round(prev.price * r.effectivePropertyTaxRate),
      insAnnual: Math.round(prev.price * r.insuranceRate),
    }));
  }, [s.autoTax, s.stateCode, s.price]);

  // ---- Server queries ----
  const zipMutation = useMutation({
    mutationFn: (z: string) => getFmrByZip({ data: { zip: z } }),
    onSuccess: (r) => setFmr(r),
  });
  const fipsMutation = useMutation({
    mutationFn: (f: string) => getFmrByFips({ data: { fips: f } }),
    onSuccess: (r) => setFmr(r),
  });
  const countiesQuery = useQuery({
    queryKey: ["counties", s.countyState],
    queryFn: () => listCounties({ data: { state: s.countyState } }),
    enabled: s.lookupType === "county" && s.countyState.length === 2,
    staleTime: 60 * 60 * 1000,
  });

  // ---- Derived ----
  const downPayment = s.price * (s.downPct / 100);
  const loanAmount = s.price - downPayment;
  const closingCosts = s.price * (s.closingCostsPct / 100);
  const cashToClose = downPayment + closingCosts;

  const monthlyRate = s.rate / 100 / 12;
  const n = s.termYears * 12;
  const amortPI = useMemo(() => {
    if (loanAmount <= 0 || monthlyRate <= 0) return 0;
    return (loanAmount * (monthlyRate * Math.pow(1 + monthlyRate, n))) / (Math.pow(1 + monthlyRate, n) - 1);
  }, [loanAmount, monthlyRate, n]);
  const ioPayment = loanAmount * monthlyRate;
  const pi = s.ioYears > 0 ? ioPayment : amortPI;

  const taxesMonthly = s.taxesAnnual / 12;
  const insMonthly = s.insAnnual / 12;
  const pitia = pi + taxesMonthly + insMonthly + s.hoaMonthly;

  // Section 8 gross rent = FMR × payment std − utility allowance (when tenant pays utils)
  const section8Gross = fmr ? Math.max(0, Math.round((fmr.rents[s.bedrooms] * s.paymentStd) / 100 - s.utilityAllowance) ) : 0;
  const grossRent =
    s.mode === "ltr"
      ? s.ltrRent
      : s.mode === "str"
        ? (s.adr * 30.4 * s.occupancyPct) / 100
        : section8Gross;

  const vacancyLoss = s.mode === "ltr" ? grossRent * (s.vacancyPct / 100) : 0;
  const mgmtCost =
    s.mode === "ltr"
      ? grossRent * (s.mgmtPct / 100)
      : s.mode === "str"
        ? grossRent * (s.strMgmtPct / 100)
        : grossRent * (s.section8MgmtPct / 100);
  const opex = s.mode === "str" ? s.strOpexMonthly : 0;
  const reserves =
    s.mode === "str" ? s.strReservesMonthly : s.mode === "section8" ? s.section8ReservesMonthly : 0;
  const noiMonthly = grossRent - vacancyLoss - mgmtCost - opex - reserves - taxesMonthly - insMonthly - s.hoaMonthly;
  const cashFlowMo = noiMonthly - pi;

  const dscr = pitia > 0 ? grossRent / pitia : 0;
  const dscrColor = dscr >= 1.2 ? "text-emerald-600" : dscr >= 1.0 ? "text-amber-600" : "text-destructive";
  const dscrTier =
    dscr >= 1.2 ? "Best pricing tier" : dscr >= 1.0 ? "Standard tier" : dscr >= 0.75 ? "Available with adjustments" : "Below program floor";

  const capRate = s.price > 0 ? (((noiMonthly + pi) * 12) / s.price) * 100 : 0;
  const cocReturn = cashToClose > 0 ? ((cashFlowMo * 12) / cashToClose) * 100 : 0;

  // STR break-even occupancy: what occupancy % makes DSCR = 1.0?
  const strMonthlyGrossForBreakeven = pitia; // rent needed to hit DSCR 1.0
  const breakEvenOccupancy =
    s.adr > 0 ? Math.min(100, (strMonthlyGrossForBreakeven / (s.adr * 30.4)) * 100) : 0;

  // Compare-all-modes (uses current inputs; only rent differs)
  const compareRents = {
    ltr: s.ltrRent,
    str: (s.adr * 30.4 * s.occupancyPct) / 100,
    section8: section8Gross,
  };
  const compareDscr = {
    ltr: pitia > 0 ? compareRents.ltr / pitia : 0,
    str: pitia > 0 ? compareRents.str / pitia : 0,
    section8: pitia > 0 ? compareRents.section8 / pitia : 0,
  };

  // Suggested LTR rent from FMR × multiplier
  const suggestedLtrRent = fmr ? Math.round(fmr.rents[s.bedrooms] * s.ltrMultiplier) : 0;

  // ---- Share URL ----
  const buildShareUrl = () => {
    const payload = btoa(JSON.stringify(s)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
    return `${window.location.origin}${window.location.pathname}?s=${payload}`;
  };
  const share = async () => {
    const url = buildShareUrl();
    try {
      await navigator.clipboard.writeText(url);
      window.history.replaceState(null, "", url);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2200);
    } catch {
      window.prompt("Copy this link:", url);
    }
  };

  return (
    <SiteShell>
      <style>{`@media print { header, footer, .no-print { display: none !important; } main { padding: 0 !important; } }`}</style>
      <PageHero
        eyebrow="Calculator"
        title="DSCR calculator"
        subtitle={`Model long-term rentals, Airbnb / short-term, and Section 8 with live ${HUD_DISPLAY_FY} HUD Fair Market Rents. DSCR, cash-on-cash, cap rate, break-even occupancy — in one place.`}
        image={{ src: heroDscrCalc, alt: "Woman on a couch at home using the Simply Approved Mortgages DSCR calculator on a laptop" }}
      />
      <RankKit page="dscr-calculator" part="answer" />
      <PageEssentials hideAnswer />

      <Section>
        {/* Mode toggle + actions */}
        <div className="mx-auto max-w-6xl">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap gap-2 rounded-xl border border-border bg-card p-2">
              <ModeButton active={s.mode === "ltr"} onClick={() => set("mode", "ltr")} label="Long-Term Rental" sub="Traditional DSCR" />
              <ModeButton active={s.mode === "str"} onClick={() => set("mode", "str")} label="Airbnb / STR" sub="ADR × occupancy" />
              <ModeButton active={s.mode === "section8"} onClick={() => set("mode", "section8")} label="Section 8" sub={`${HUD_DISPLAY_FY} HUD FMR`} />
            </div>
            <div className="no-print flex flex-wrap gap-2">
              <IconBtn
                onClick={() => (leadUnlocked ? share() : setGate("share"))}
                label={leadUnlocked ? (shareCopied ? "Link copied ✓" : "Share") : "🔒 Share"}
              />
              <IconBtn
                onClick={() => (leadUnlocked ? window.print() : setGate("print"))}
                label={leadUnlocked ? "Print / PDF" : "🔒 Print / PDF"}
              />
              <IconBtn
                onClick={() => (leadUnlocked ? setEmailOpen(true) : setGate("email"))}
                label={leadUnlocked ? "Email me this" : "🔒 Email me this"}
                primary
              />
            </div>
          </div>
          {!leadUnlocked && (
            <p className="no-print mt-2 text-right text-xs text-muted-foreground">
              Share, print & email unlock after you save this scenario (30 seconds).
            </p>
          )}
        </div>

        <div className="mx-auto mt-8 grid max-w-6xl gap-8 lg:grid-cols-[1.3fr_1fr]">
          {/* Inputs */}
          <div className="space-y-6">
            <Card title="Property & loan">
              <div className="grid gap-4 sm:grid-cols-2">
                <NumField label="Purchase price ($)" value={s.price} setValue={(v) => set("price", v)} step={1000} />
                <NumField label="Down payment (%)" value={s.downPct} setValue={(v) => set("downPct", v)} step={1} suffix="%" />
                <ReadTile label="Loan amount" value={fmt$(loanAmount)} />
                <ReadTile label="Down payment" value={fmt$(downPayment)} />
                <NumField label="Interest rate (%)" value={s.rate} setValue={(v) => set("rate", v)} step={0.125} suffix="%" />
                <NumField label="Term (years)" value={s.termYears} setValue={(v) => set("termYears", v)} step={1} />
                <SelectField
                  label="Interest-only period"
                  value={String(s.ioYears)}
                  onChange={(v) => set("ioYears", Number(v))}
                  options={[
                    { value: "0", label: "None (fully amortizing)" },
                    { value: "5", label: "5-year I/O" },
                    { value: "10", label: "10-year I/O" },
                  ]}
                />
                <SelectField
                  label="Prepay penalty"
                  value={s.prepay}
                  onChange={(v) => set("prepay", v)}
                  options={PREPAY_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
                />
                <NumField label="Closing costs (%)" value={s.closingCostsPct} setValue={(v) => set("closingCostsPct", v)} step={0.25} suffix="%" />
                <ReadTile label="Total cash to close" value={fmt$(cashToClose)} highlight />
              </div>
            </Card>

            <Card title="Taxes, insurance, HOA">
              <div className="mb-3 flex flex-wrap items-end gap-3">
                <SelectField
                  label="State (auto-fills tax + insurance)"
                  value={s.stateCode}
                  onChange={(v) => set("stateCode", v)}
                  options={[{ value: "", label: "Select state…" }, ...STATE_RATES.map((r) => ({ value: r.code, label: r.name }))]}
                />
                <label className="flex items-center gap-2 pb-2 text-sm text-foreground">
                  <input
                    type="checkbox"
                    checked={s.autoTax}
                    onChange={(e) => set("autoTax", e.target.checked)}
                    disabled={!s.stateCode}
                    className="h-4 w-4 rounded border-input"
                  />
                  Auto-estimate for {s.stateCode || "state"}
                </label>
              </div>
              <div className="grid gap-4 sm:grid-cols-3">
                <NumField label="Taxes ($/yr)" value={s.taxesAnnual} setValue={(v) => set("taxesAnnual", v)} step={100} />
                <NumField label="Insurance ($/yr)" value={s.insAnnual} setValue={(v) => set("insAnnual", v)} step={50} />
                <NumField label="HOA ($/mo)" value={s.hoaMonthly} setValue={(v) => set("hoaMonthly", v)} step={25} />
              </div>
            </Card>

            {s.mode === "ltr" && (
              <Card title="Long-term rent">
                <div className="grid gap-4 sm:grid-cols-3">
                  <NumField label="Monthly rent ($)" value={s.ltrRent} setValue={(v) => set("ltrRent", v)} step={50} />
                  <NumField label="Vacancy (%)" value={s.vacancyPct} setValue={(v) => set("vacancyPct", v)} step={1} suffix="%" />
                  <NumField label="Management (%)" value={s.mgmtPct} setValue={(v) => set("mgmtPct", v)} step={1} suffix="%" />
                </div>

                <div className="mt-4 rounded-md border border-border bg-muted/30 p-3">
                  <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Prefill rent from {HUD_DISPLAY_FY} HUD Fair Market Rent
                  </div>
                  <div className="grid gap-3 sm:grid-cols-[1fr_1fr_auto]">
                    <label className="block">
                      <span className="mb-1 block text-xs font-medium text-foreground">ZIP code</span>
                      <input
                        type="text"
                        inputMode="numeric"
                        maxLength={5}
                        value={s.zip}
                        onChange={(e) => set("zip", e.target.value.replace(/\D/g, "").slice(0, 5))}
                        placeholder="e.g. 30318"
                        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      />
                    </label>
                    <BedroomSelect value={s.bedrooms} onChange={(v) => set("bedrooms", v)} />
                    <button
                      onClick={() => s.zip.length === 5 && zipMutation.mutate(s.zip)}
                      disabled={s.zip.length !== 5 || zipMutation.isPending}
                      className="self-end rounded-md bg-brand px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-brand/90 disabled:opacity-50"
                    >
                      {zipMutation.isPending ? "Loading…" : "Look up FMR"}
                    </button>
                  </div>
                  {zipMutation.error && (
                    <p className="mt-2 text-xs text-destructive">{(zipMutation.error as Error).message}</p>
                  )}
                </div>

                {fmr && (
                  <div className="mt-4 flex flex-wrap items-center gap-3 rounded-md border border-brand/30 bg-brand/5 p-3 text-sm">
                    <span className="text-muted-foreground">
                      HUD FMR ({BR_LABEL[s.bedrooms]}) ${fmr.rents[s.bedrooms].toLocaleString()} × {s.ltrMultiplier.toFixed(2)}× market =
                    </span>
                    <span className="font-bold text-foreground">${suggestedLtrRent.toLocaleString()}/mo</span>
                    <button
                      onClick={() => set("ltrRent", suggestedLtrRent)}
                      className="rounded-md bg-brand px-3 py-1 text-xs font-semibold text-primary-foreground hover:bg-brand/90"
                    >
                      Use this rent
                    </button>
                    <div className="basis-full">
                      <NumField
                        label="FMR-to-market multiplier (LTR typically rents 10–20% above FMR)"
                        value={s.ltrMultiplier}
                        setValue={(v) => set("ltrMultiplier", v)}
                        step={0.05}
                      />
                    </div>
                  </div>
                )}
              </Card>
            )}


            {s.mode === "str" && (
              <Card title="Short-term rental (Airbnb / VRBO)">
                <div className="grid gap-4 sm:grid-cols-2">
                  <NumField label="Average daily rate ($)" value={s.adr} setValue={(v) => set("adr", v)} step={5} />
                  <NumField label="Occupancy (%)" value={s.occupancyPct} setValue={(v) => set("occupancyPct", v)} step={1} suffix="%" />
                  <NumField label="STR management (%)" value={s.strMgmtPct} setValue={(v) => set("strMgmtPct", v)} step={1} suffix="%" />
                  <NumField label="Cleaning + utilities ($/mo)" value={s.strOpexMonthly} setValue={(v) => set("strOpexMonthly", v)} step={25} />
                  <NumField label="Reserves / replacement fund ($/mo)" value={s.strReservesMonthly} setValue={(v) => set("strReservesMonthly", v)} step={25} />
                </div>
                <div className="mt-4 rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-sm">
                  <span className="text-muted-foreground">Break-even occupancy (DSCR = 1.0): </span>
                  <span className="font-bold text-foreground">{breakEvenOccupancy.toFixed(1)}%</span>
                  {breakEvenOccupancy > 100 && (
                    <span className="ml-2 text-destructive">— property can't hit DSCR 1.0 at any occupancy at this ADR.</span>
                  )}
                </div>
              </Card>
            )}

            {s.mode === "section8" && (
              <Card title={`Section 8 · ${HUD_DISPLAY_FY} HUD Fair Market Rent`}>
                <div className="mb-4 flex gap-2 rounded-md border border-border bg-muted/30 p-1 text-sm">
                  <button
                    onClick={() => set("lookupType", "zip")}
                    className={`flex-1 rounded px-3 py-1.5 font-semibold ${s.lookupType === "zip" ? "bg-brand text-primary-foreground" : "text-muted-foreground"}`}
                  >
                    By ZIP (Small Area FMR)
                  </button>
                  <button
                    onClick={() => set("lookupType", "county")}
                    className={`flex-1 rounded px-3 py-1.5 font-semibold ${s.lookupType === "county" ? "bg-brand text-primary-foreground" : "text-muted-foreground"}`}
                  >
                    By county
                  </button>
                </div>

                {s.lookupType === "zip" ? (
                  <div className="grid gap-4 sm:grid-cols-[1fr_1fr_auto]">
                    <label className="block">
                      <span className="mb-1 block text-sm font-medium text-foreground">ZIP code</span>
                      <input
                        type="text"
                        inputMode="numeric"
                        maxLength={5}
                        value={s.zip}
                        onChange={(e) => set("zip", e.target.value.replace(/\D/g, "").slice(0, 5))}
                        placeholder="e.g. 30318"
                        className="w-full rounded-md border border-input bg-background px-3 py-2 text-lg"
                      />
                    </label>
                    <BedroomSelect value={s.bedrooms} onChange={(v) => set("bedrooms", v)} />
                    <button
                      onClick={() => s.zip.length === 5 && zipMutation.mutate(s.zip)}
                      disabled={s.zip.length !== 5 || zipMutation.isPending}
                      className="self-end rounded-md bg-brand px-4 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-brand/90 disabled:opacity-50"
                    >
                      {zipMutation.isPending ? "Loading…" : "Look up FMR"}
                    </button>
                  </div>
                ) : (
                  <div className="grid gap-4 sm:grid-cols-[1fr_2fr_1fr_auto]">
                    <SelectField
                      label="State"
                      value={s.countyState}
                      onChange={(v) => {
                        set("countyState", v);
                        set("countyFips", "");
                      }}
                      options={[{ value: "", label: "Select…" }, ...STATE_RATES.map((r) => ({ value: r.code, label: r.name }))]}
                    />
                    <SelectField
                      label={countiesQuery.isFetching ? "County (loading…)" : "County"}
                      value={s.countyFips}
                      onChange={(v) => set("countyFips", v)}
                      options={[
                        { value: "", label: countiesQuery.data ? "Select county…" : "Pick a state first" },
                        ...(countiesQuery.data ?? []).map((c) => ({
                          value: c.fipsCode,
                          label: c.townName ? `${c.countyName} — ${c.townName}` : c.countyName,
                        })),
                      ]}
                    />
                    <BedroomSelect value={s.bedrooms} onChange={(v) => set("bedrooms", v)} />
                    <button
                      onClick={() => s.countyFips && fipsMutation.mutate(s.countyFips)}
                      disabled={!s.countyFips || fipsMutation.isPending}
                      className="self-end rounded-md bg-brand px-4 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-brand/90 disabled:opacity-50"
                    >
                      {fipsMutation.isPending ? "Loading…" : "Look up FMR"}
                    </button>
                  </div>
                )}

                {(zipMutation.error || fipsMutation.error) && (
                  <p className="mt-3 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
                    {((zipMutation.error || fipsMutation.error) as Error).message}
                  </p>
                )}

                {fmr && (
                  <div className="mt-4 rounded-lg border border-brand/30 bg-brand/5 p-4">
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <div className="text-sm font-semibold text-foreground">
                        {fmr.smallArea ? "Small Area FMR" : "Metro / County FMR"} · {fmr.year}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {fmr.county || fmr.metroName || fmr.key}
                        {fmr.state ? `, ${fmr.state}` : ""}
                      </div>
                    </div>
                    <div className="mt-3 grid grid-cols-5 gap-2 text-center text-xs">
                      {(Object.keys(BR_LABEL) as Bedrooms[]).map((k) => (
                        <button
                          key={k}
                          onClick={() => set("bedrooms", k)}
                          className={`rounded-md border p-2 transition-colors ${k === s.bedrooms ? "border-brand-gold bg-brand-gold/10 text-foreground" : "border-border text-muted-foreground hover:border-brand"}`}
                        >
                          <div className="font-semibold">{BR_LABEL[k]}</div>
                          <div className="mt-1 text-sm font-bold text-foreground">${fmr.rents[k].toLocaleString()}</div>
                        </button>
                      ))}
                    </div>
                    <div className="mt-4 grid gap-4 sm:grid-cols-2">
                      <NumField
                        label="PHA payment standard (% of FMR)"
                        value={s.paymentStd}
                        setValue={(v) => set("paymentStd", v)}
                        step={1}
                        suffix="%"
                      />
                      <NumField
                        label="Utility allowance ($/mo, if tenant-paid)"
                        value={s.utilityAllowance}
                        setValue={(v) => set("utilityAllowance", v)}
                        step={10}
                      />
                      <NumField
                        label="Management (%)"
                        value={s.section8MgmtPct}
                        setValue={(v) => set("section8MgmtPct", v)}
                        step={1}
                        suffix="%"
                      />
                      <NumField
                        label="Reserves / replacement fund ($/mo)"
                        value={s.section8ReservesMonthly}
                        setValue={(v) => set("section8ReservesMonthly", v)}
                        step={25}
                      />
                    </div>
                    <div className="mt-3 text-sm text-muted-foreground">
                      Estimated Section 8 gross rent: <span className="font-semibold text-foreground">${section8Gross.toLocaleString()}/mo</span>
                    </div>
                    <p className="mt-3 text-xs text-muted-foreground">
                      Source: HUD User FMR API. Actual contract rent must also pass the housing authority's rent reasonableness test.
                    </p>
                  </div>
                )}
                {!fmr && (
                  <p className="mt-3 text-xs text-muted-foreground">
                    Enter a ZIP for Small Area FMR, or use the county picker for any US county.
                    <a href="/section-8-investing-map" className="ml-1 font-semibold text-brand hover:underline">
                      Browse the Section 8 investing map →
                    </a>
                  </p>
                )}
              </Card>
            )}
          </div>

          {/* Results */}
          <div className="lg:sticky lg:top-24 lg:self-start">
            <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Your DSCR</div>
              <div className={`mt-1 font-display text-6xl font-bold ${dscrColor}`}>{dscr.toFixed(2)}</div>
              <div className="mt-1 text-sm font-semibold text-foreground">{dscrTier}</div>

              <hr className="my-5 border-border" />

              <dl className="space-y-2.5 text-sm">
                <Row label="Gross rent" value={`${fmt$(grossRent)}/mo`} strong />
                <Row label={s.ioYears > 0 ? `Interest-only (${s.ioYears}-yr)` : "Principal & interest"} value={`${fmt$(pi)}/mo`} />
                <Row label="Taxes" value={`${fmt$(taxesMonthly)}/mo`} />
                <Row label="Insurance" value={`${fmt$(insMonthly)}/mo`} />
                {s.hoaMonthly > 0 && <Row label="HOA" value={`${fmt$(s.hoaMonthly)}/mo`} />}
                <Row label="PITIA" value={`${fmt$(pitia)}/mo`} strong />
                {s.mode === "ltr" && <Row label={`Vacancy (${s.vacancyPct}%)`} value={`-${fmt$(vacancyLoss)}/mo`} />}
                {mgmtCost > 0 && <Row label="Management" value={`-${fmt$(mgmtCost)}/mo`} />}
                {s.mode === "str" && <Row label="Cleaning/utilities" value={`-${fmt$(opex)}/mo`} />}
                {reserves > 0 && <Row label="Reserves" value={`-${fmt$(reserves)}/mo`} />}
                <Row label="Est. cash flow" value={`${fmt$(cashFlowMo)}/mo`} strong />
              </dl>

              <hr className="my-5 border-border" />

              <div className="grid grid-cols-2 gap-3">
                <StatTile label="Cap rate" value={`${capRate.toFixed(2)}%`} />
                <StatTile label="Cash-on-cash" value={`${cocReturn.toFixed(2)}%`} />
                <StatTile label="Cash to close" value={fmt$(cashToClose)} />
                <StatTile label="Loan amount" value={fmt$(loanAmount)} />
              </div>

              <div className="mt-5 space-y-2 text-xs">
                <TierRow label="0.75 – 0.99" note="Available with adjustments" active={dscr >= 0.75 && dscr < 1.0} />
                <TierRow label="1.00 – 1.19" note="Standard tier" active={dscr >= 1.0 && dscr < 1.2} />
                <TierRow label="1.20+" note="Best pricing" active={dscr >= 1.2} />
              </div>

              <a
                href="/apply"
                className="mt-5 block rounded-md bg-brand-gold px-4 py-3 text-center text-sm font-semibold text-primary hover:bg-brand-gold-hover"
              >Start My Approval</a>
              <p className="mt-3 text-[11px] leading-relaxed text-muted-foreground">
                Estimates only. Final DSCR and pricing depend on program guidelines, credit tier, property type, and market. Not a commitment
                to lend.
              </p>
            </div>
          </div>
        </div>

        {/* Compare all three modes */}
        <div className="mx-auto mt-12 max-w-6xl">
          <div className="rounded-xl border border-border bg-card p-6">
            <div className="mb-4">
              <div className="text-xs uppercase tracking-wide text-brand-gold">Strategy compare</div>
              <div className="mt-1 font-display text-xl font-bold text-foreground">Same property, three exit strategies</div>
              <p className="mt-1 text-sm text-muted-foreground">
                Using your current price, loan, and expenses — how does DSCR change across LTR, Airbnb, and Section 8?
              </p>
            </div>
            <div className="grid gap-4 md:grid-cols-3">
              <CompareCard label="Long-Term Rental" rent={compareRents.ltr} dscr={compareDscr.ltr} pitia={pitia} />
              <CompareCard label="Airbnb / STR" rent={compareRents.str} dscr={compareDscr.str} pitia={pitia} />
              <CompareCard
                label="Section 8"
                rent={compareRents.section8}
                dscr={compareDscr.section8}
                pitia={pitia}
                note={fmr ? undefined : "Look up FMR to see this"}
              />
            </div>
          </div>
        </div>
      </Section>

      {emailOpen && (
        <EmailScenarioModal
          scenario={s}
          summary={{ dscr, pitia, grossRent, cashFlowMo, capRate, cocReturn, cashToClose, loanAmount, pi }}
          onClose={() => setEmailOpen(false)}
        />
      )}

      {gate && (
        <LeadGateModal
          reason={gate}
          onClose={() => setGate(null)}
          getCalculatorContext={() => ({
            mode: s.mode,
            price: s.price,
            downPct: s.downPct,
            rate: s.rate,
            termYears: s.termYears,
            ioYears: s.ioYears,
            grossRent: Math.round(grossRent),
            pitia: Math.round(pitia),
            dscr: Number(dscr.toFixed(2)),
            capRate: Number(capRate.toFixed(2)),
            cashOnCash: Number(cocReturn.toFixed(2)),
            cashFlowMonthly: Math.round(cashFlowMo),
            cashToClose: Math.round(cashToClose),
            loanAmount: Math.round(loanAmount),
            zip: s.zip || null,
            state: s.stateCode || s.countyState || null,
            bedrooms: s.bedrooms,
            scenarioShareUrl: typeof window !== "undefined"
              ? `${window.location.origin}${window.location.pathname}?s=${btoa(JSON.stringify(s)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "")}`
              : "",
          })}
          onSuccess={() => {
            setLeadUnlocked(true);
            if (typeof window !== "undefined") window.localStorage.setItem(LEAD_UNLOCK_KEY, "1");
            const next = gate;
            setGate(null);
            // Auto-run the action they originally wanted.
            setTimeout(() => {
              if (next === "share") share();
              else if (next === "print") window.print();
              else if (next === "email") setEmailOpen(true);
            }, 300);
          }}
        />
      )}


      <RankKit page="dscr-calculator" part="details" />
      <LiveDscrRateSection
            strategyCards
        id="live-rates"
        eyebrow="Live pricing · Calculator"
        heading="Turn Your Calculated DSCR Into a Live Rate"
        paragraphs={[
          "A calculated ratio only tells you half the story — the other half is what the ratio prices at. The options below are the most recent live quotes returned by the pricing engine for a qualifying rental scenario, with the rate, APR, points or lender credit and monthly payment all taken from the same quote.",
          "If the payment you modeled above assumed a rate, replace it: price your actual leverage, credit band and property profile in the rate center and re-run the ratio with a real payment instead of an assumption.",
        ]}
        faqs={[
          { q: "Why doesn't the calculator show a rate automatically?", a: "The calculator does not price loans. We only display rates returned by the pricing engine for a specific scenario, so nothing on this page is an estimated or sample rate figure." },
          { q: "Which payment should I use in the DSCR formula?", a: "Use the full PITIA — principal and interest plus taxes, insurance, HOA dues and, where applicable, flood insurance. The monthly figure shown with each live quote is principal and interest only." },
        ]}
        links={[
          { to: "/investment-property-rates", label: "Price your scenario in the DSCR rate center" },
          { to: "/how-dscr-works", label: "How the DSCR calculation works" },
          { to: "/dscr-loan-requirements", label: "DSCR loan requirements" },
        ]}
      />

      <CTABand
        title="Numbers borderline? We'll help you get over the line."
        body="Submit a complete application with address, ZIP, rents, credit authorization, and supporting docs — we.ll model DSCR across long-term, Airbnb, and Section 8 exit strategies and reply with an initial scenario review. Formal pricing follows underwriting."
      />
    <PageOutro hideLeadForm />
      </SiteShell>
  );
}

// ---------- Small components ----------
function ModeButton({ active, onClick, label, sub }: { active: boolean; onClick: () => void; label: string; sub: string }) {
  return (
    <button
      onClick={onClick}
      className={`flex-1 rounded-lg px-4 py-3 text-left transition-colors ${
        active ? "bg-brand text-primary-foreground shadow-sm" : "bg-transparent text-foreground hover:bg-muted"
      }`}
    >
      <div className="font-display text-base font-semibold">{label}</div>
      <div className={`text-xs ${active ? "text-primary-foreground/80" : "text-muted-foreground"}`}>{sub}</div>
    </button>
  );
}
function IconBtn({ onClick, label, primary }: { onClick: () => void; label: string; primary?: boolean }) {
  return (
    <button
      onClick={onClick}
      className={`rounded-md px-3 py-2 text-sm font-semibold transition-colors ${
        primary ? "bg-brand-gold text-primary hover:bg-brand-gold-hover" : "border border-border bg-card text-foreground hover:border-brand"
      }`}
    >
      {label}
    </button>
  );
}
function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
      <div className="mb-4 font-display text-lg font-semibold text-foreground">{title}</div>
      {children}
    </div>
  );
}
function NumField({
  label, value, setValue, step = 1, suffix,
}: { label: string; value: number; setValue: (v: number) => void; step?: number; suffix?: string }) {
  const [text, setText] = useState<string>(String(value));
  const lastEmitted = useRef<number>(value);
  // Only sync from prop when it changed from an EXTERNAL source (not our own onChange).
  useEffect(() => {
    if (value !== lastEmitted.current) {
      setText(String(value));
      lastEmitted.current = value;
    }
  }, [value]);
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-foreground">{label}</span>
      <div className="relative">
        <input
          type="text"
          inputMode="decimal"
          step={step}
          value={text}
          onFocus={(e) => e.target.select()}
          onChange={(e) => {
            const raw = e.target.value.replace(/[^0-9.]/g, "");
            setText(raw);
            const n = raw === "" || raw === "." ? 0 : Number(raw);
            lastEmitted.current = n;
            setValue(n);
          }}
          className={`w-full rounded-md border border-input bg-background px-3 py-2 text-lg ${suffix ? "pr-9" : ""}`}
        />
        {suffix && <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">{suffix}</span>}
      </div>
    </label>
  );
}
function SelectField({
  label, value, onChange, options,
}: { label: string; value: string; onChange: (v: string) => void; options: { value: string; label: string }[] }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-foreground">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-md border border-input bg-background px-3 py-2 text-base"
      >
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}
function BedroomSelect({ value, onChange }: { value: Bedrooms; onChange: (v: Bedrooms) => void }) {
  return (
    <SelectField
      label="Bedrooms"
      value={value}
      onChange={(v) => onChange(v as Bedrooms)}
      options={(Object.keys(BR_LABEL) as Bedrooms[]).map((k) => ({ value: k, label: BR_LABEL[k] }))}
    />
  );
}
function ReadTile({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className={`rounded-md border p-3 ${highlight ? "border-brand-gold/40 bg-brand-gold/10" : "border-border bg-muted/40"}`}>
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-0.5 font-display text-lg font-semibold text-foreground">{value}</div>
    </div>
  );
}
function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <dt className={`text-muted-foreground ${strong ? "font-semibold text-foreground" : ""}`}>{label}</dt>
      <dd className={`tabular-nums ${strong ? "font-semibold text-foreground" : "text-foreground"}`}>{value}</dd>
    </div>
  );
}
function StatTile({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border bg-muted/40 p-3">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-0.5 font-display text-base font-bold text-foreground tabular-nums">{value}</div>
    </div>
  );
}
function TierRow({ label, note, active }: { label: string; note: string; active: boolean }) {
  return (
    <div className={`flex items-center justify-between rounded-md border px-3 py-2 ${active ? "border-brand-gold bg-brand-gold/10 text-foreground" : "border-border text-muted-foreground"}`}>
      <span className="font-semibold">{label}</span>
      <span>{note}</span>
    </div>
  );
}
function CompareCard({ label, rent, dscr, pitia, note }: { label: string; rent: number; dscr: number; pitia: number; note?: string }) {
  const color = dscr >= 1.2 ? "text-emerald-600" : dscr >= 1.0 ? "text-amber-600" : "text-destructive";
  return (
    <div className="rounded-lg border border-border bg-background p-4">
      <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`mt-1 font-display text-3xl font-bold ${color}`}>{note ? "—" : dscr.toFixed(2)}</div>
      <div className="mt-1 text-xs text-muted-foreground">
        {note ?? `Rent ${fmt$(rent)}/mo · PITIA ${fmt$(pitia)}/mo`}
      </div>
    </div>
  );
}

function EmailScenarioModal({
  scenario, summary, onClose,
}: {
  scenario: Scenario;
  summary: { dscr: number; pitia: number; grossRent: number; cashFlowMo: number; capRate: number; cocReturn: number; cashToClose: number; loanAmount: number; pi: number };
  onClose: () => void;
}) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [sent, setSent] = useState(false);
  const [waitlisted, setWaitlisted] = useState(false);
  const mutation = useMutation({
    mutationFn: (payload: LeadInput) => submitLead({ data: payload }),
    onSuccess: (res: { leadId?: string | null; waitlisted?: boolean } | undefined) => {
      // State outside our footprint → educational waitlist, not a conversion.
      if (res?.waitlisted) {
        setWaitlisted(true);
        setSent(true);
        return;
      }
      void import("@/lib/tracking").then((m) =>
        m.trackLeadConversion({
          product: "dscr",
          productLabel: "DSCR calculator scenario",
          leadId: res?.leadId ?? undefined,
          state: scenario.stateCode || undefined,
          form: "dscr_calculator",
        }),
      );
      setSent(true);
    },
  });
  const submit = () => {
    if (!name || !email) return;
    const shareUrl = (() => {
      const payload = btoa(JSON.stringify(scenario)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
      return `${window.location.origin}${window.location.pathname}?s=${payload}`;
    })();
    const modeLabel = scenario.mode === "ltr" ? "Long-Term Rental" : scenario.mode === "str" ? "Airbnb / STR" : "Section 8";
    const message = [
      `DSCR calculator scenario (${modeLabel})`,
      `DSCR: ${summary.dscr.toFixed(2)}   |   PITIA: ${fmt$(summary.pitia)}/mo`,
      `Gross rent: ${fmt$(summary.grossRent)}/mo   |   Cash flow: ${fmt$(summary.cashFlowMo)}/mo`,
      `Cap rate: ${summary.capRate.toFixed(2)}%   |   Cash-on-cash: ${summary.cocReturn.toFixed(2)}%`,
      `Price: ${fmt$(scenario.price)}   |   Down: ${scenario.downPct}%   |   Rate: ${scenario.rate}%   |   Term: ${scenario.termYears}y${scenario.ioYears ? ` (${scenario.ioYears}y I/O)` : ""}`,
      `Prepay: ${scenario.prepay}   |   Cash to close: ${fmt$(summary.cashToClose)}   |   Loan: ${fmt$(summary.loanAmount)}`,
      "",
      `Share link: ${shareUrl}`,
    ].join("\n");
    mutation.mutate({
      name,
      email,
      phone,
      product: `DSCR Calculator — ${modeLabel}`,
      state: scenario.stateCode,
      loan_amount: Math.round(summary.loanAmount),
      estimated_value: scenario.price,
      message,
      source_page: "dscr-calculator",
      source_url: window.location.href,
      ...captureUtmParams(),
    });
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-xl bg-card p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        {sent ? (
          <div className="text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-2xl">✓</div>
            <h3 className="mt-3 font-display text-xl font-bold text-foreground">{waitlisted ? "You're on the waitlist" : "Scenario sent"}</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              {waitlisted
                ? "We don't arrange financing in that property state yet, so no loan officer will contact you. Your calculator results stay free to use."
                : "We'll be in touch shortly with pricing feedback."}
            </p>
            <button onClick={onClose} className="mt-5 rounded-md bg-brand-gold px-4 py-2 text-sm font-semibold text-primary">Close</button>
          </div>
        ) : (
          <>
            <div className="flex items-start justify-between">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-brand-gold">Email me this scenario</div>
                <h3 className="mt-1 font-display text-xl font-bold text-foreground">Get an initial scenario review</h3>
              </div>
              <button onClick={onClose} className="text-muted-foreground hover:text-foreground" aria-label="Close">×</button>
            </div>
            <p className="mt-2 text-sm text-muted-foreground">We'll email you back with an initial scenario review once we have your application, credit authorization, and supporting docs — usually the same business day. Formal pricing follows underwriting.</p>
            <div className="mt-4 space-y-3">
              <input placeholder="Full name" value={name} onChange={(e) => setName(e.target.value)} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
              <input placeholder="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
              <input placeholder="Phone (optional)" value={phone} onChange={(e) => setPhone(e.target.value)} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
              {mutation.error && <p className="text-sm text-destructive">{(mutation.error as Error).message}</p>}
              <button
                onClick={submit}
                disabled={!name || !email || mutation.isPending}
                className="w-full rounded-md bg-brand-gold px-4 py-2.5 text-sm font-semibold text-primary hover:bg-brand-gold-hover disabled:opacity-50"
              >
                {mutation.isPending ? "Sending…" : "Send scenario"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const fmt$ = (n: number) => `$${Math.round(n).toLocaleString()}`;

function LeadGateModal({
  reason,
  onClose,
  onSuccess,
  getCalculatorContext,
}: {
  reason: "share" | "print" | "email";
  onClose: () => void;
  onSuccess: () => void;
  getCalculatorContext: () => Record<string, string | number | boolean | null>;
}) {
  const headline =
    reason === "share"
      ? "Save your DSCR scenario to share"
      : reason === "print"
        ? "Save your DSCR scenario to print"
        : "Save your DSCR scenario to email";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm p-4">
      <div className="w-full max-w-2xl overflow-hidden rounded-xl border border-border bg-card shadow-2xl">
        <div className="flex items-start justify-between border-b border-border p-5">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-brand">Quick save</div>
            <h3 className="mt-1 font-display text-xl font-bold text-foreground">{headline}</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              A loan officer will follow up within one business day. Your full scenario is attached.
            </p>
          </div>
          <button onClick={onClose} className="rounded-md p-2 text-muted-foreground hover:bg-muted" aria-label="Close">
            ✕
          </button>
        </div>
        <div className="max-h-[75vh] overflow-y-auto p-5">
          <LeadForm
            compact
            defaultProduct="DSCR"
            sourcePage="DSCR Calculator"
            getCalculatorContext={getCalculatorContext}
            onSuccess={() => onSuccess()}
          />
        </div>
      </div>
    </div>
  );
}
